import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, Building2, ShieldAlert, Award, Users, Timer, Radio } from "lucide-react";

/**
 * Executive Intelligence Centre — /admin/intelligence
 * Cross-collection KPI dashboard + AI narrative + recommendations.
 * Companion live view at /admin/situation-room.
 */
export default function ExecutiveIntelligence() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ period_days: String(period) });
      if (country) params.set("country", country);
      const r = await api.get(`/executive/intelligence?${params.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, country]);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="kicker">Executive · Intelligence Centre</div>
            <h1 className="display text-4xl">Africa-Wide KPIs, Risk &amp; Recommendations</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Cross-country verification activity, escrow flow, certificate issuance, SLA compliance, inspector productivity, fraud exposure and AI narrative — refreshed on demand across the entire THOM Verify™ platform.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <select className="h-9 border border-border bg-background text-[12.5px] px-2 rounded-sm" value={period} onChange={(e) => setPeriod(Number(e.target.value))} data-testid="intel-period">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            {["super_admin", "admin", "executive", "finance_manager"].includes(user?.role) && (
              <select className="h-9 border border-border bg-background text-[12.5px] px-2 rounded-sm" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="intel-country">
                <option value="">All countries</option>
                {["Zimbabwe","South Africa","Kenya","Nigeria","Ghana","Botswana","Zambia","Namibia","Uganda","Tanzania","Rwanda","Mozambique","Egypt","Morocco"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={load} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            <Link to="/admin/situation-room"><Button size="sm" data-testid="intel-situation-link"><Radio className="h-3.5 w-3.5 mr-1" /> Situation Room</Button></Link>
          </div>
        </header>

        {/* KPI strip */}
        <div className="grid md:grid-cols-4 gap-4 mb-8" data-testid="intel-kpi-strip">
          <K icon={TrendingUp} label="Verification requests"    value={data?.verification.total} sub={`${data?.verification.new || 0} new · ${data?.verification.pending || 0} pending`} />
          <K icon={Building2}  label="Active escrows"            value={data?.escrow.active_count} sub={`Balance USD ${(data?.escrow.active_balance_usd || 0).toLocaleString()}`} />
          <K icon={Award}      label="Certificates issued"       value={data?.certificates.issued_in_period} sub={`${data?.certificates.active || 0} active · ${data?.certificates.revoked_or_suspended || 0} revoked/suspended`} />
          <K icon={Timer}      label="SLA compliance"            value={data?.sla?.on_time_pct != null ? `${data.sla.on_time_pct}%` : "—"} sub={`${data?.sla?.sample_size || 0} sampled requests`} tone={
            data?.sla?.on_time_pct == null ? "neutral"
            : data.sla.on_time_pct >= 85 ? "good"
            : data.sla.on_time_pct >= 60 ? "warn" : "bad"} />
        </div>

        {/* AI narrative + recommendations */}
        <div className="border border-border bg-card p-5 mb-8" data-testid="intel-narrative">
          <div className="kicker">Executive Narrative</div>
          <p className="mt-2 text-[14px] leading-relaxed">{data?.narrative || "Loading…"}</p>
          {(data?.recommendations || []).length > 0 && (
            <div className="mt-4">
              <div className="kicker">Recommendations</div>
              <ul className="mt-2 space-y-1.5 text-[13px]">
                {data.recommendations.map((r, i) => (
                  <li key={i} className="border-l-2 border-primary/60 pl-3">{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Two-column: Countries + Asset categories */}
        <div className="grid lg:grid-cols-2 gap-4 mb-8">
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border kicker">Country performance</div>
            <table className="w-full text-[13px]">
              <thead className="bg-muted/40"><tr className="text-left"><th className="p-2.5">Country</th><th className="p-2.5">Requests</th><th className="p-2.5">Verified</th><th className="p-2.5">Rate</th></tr></thead>
              <tbody className="divide-y divide-border">
                {(data?.countries || []).map((c) => (
                  <tr key={c.country || "unknown"} data-testid={`intel-country-${c.country || 'unknown'}`}>
                    <td className="p-2.5">{c.country || "—"}</td>
                    <td className="p-2.5">{c.requests}</td>
                    <td className="p-2.5">{c.verified}</td>
                    <td className="p-2.5 mono text-[10.5px]">{c.requests ? `${Math.round(100 * c.verified / c.requests)}%` : "—"}</td>
                  </tr>
                ))}
                {(data?.countries || []).length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground text-sm">No country data in scope.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border kicker">Asset category distribution</div>
            <table className="w-full text-[13px]">
              <thead className="bg-muted/40"><tr className="text-left"><th className="p-2.5">Category</th><th className="p-2.5">Requests</th></tr></thead>
              <tbody className="divide-y divide-border">
                {(data?.asset_categories || []).map((a) => (
                  <tr key={a.asset_category}><td className="p-2.5 uppercase mono text-[11px]">{a.asset_category}</td><td className="p-2.5">{a.count}</td></tr>
                ))}
                {(data?.asset_categories || []).length === 0 && <tr><td colSpan={2} className="p-4 text-center text-muted-foreground text-sm">No asset activity in scope.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Inspectors + Risk */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><div className="kicker">Top inspectors (period)</div></div>
            <table className="w-full text-[13px]">
              <thead className="bg-muted/40"><tr className="text-left"><th className="p-2.5">Inspector</th><th className="p-2.5">Assignments</th><th className="p-2.5">Completed</th></tr></thead>
              <tbody className="divide-y divide-border">
                {(data?.inspectors_top || []).map((i) => (
                  <tr key={i.inspector_id}><td className="p-2.5 mono text-[10.5px]">{i.inspector_id}</td><td className="p-2.5">{i.assignments}</td><td className="p-2.5">{i.completed}</td></tr>
                ))}
                {(data?.inspectors_top || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground text-sm">No inspector activity in scope.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3"><ShieldAlert className="h-4 w-4 text-primary" /><div className="kicker">Risk &amp; Disputes</div></div>
            <div className="grid grid-cols-2 gap-3">
              <Metric label="Risk / fraud audit events" value={data?.risk.audit_events} />
              <Metric label="Disputes open" value={data?.risk.disputes_open} />
              <Metric label="Verification turnaround" value={data?.turnaround ? `${data.turnaround.average_hours} h` : "—"} sub={data?.turnaround ? `${data.turnaround.sample_size} sampled` : ""} />
              <Metric label="Escrows released (period)" value={data?.escrow.released_in_period} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const K = ({ icon: Icon, label, value, sub, tone = "neutral" }) => {
  const T = { good: "text-emerald-700 border-emerald-500/20 bg-emerald-500/5",
              warn: "text-amber-700 border-amber-500/20 bg-amber-500/5",
              bad:  "text-red-700 border-red-500/20 bg-red-500/5",
              neutral: "border-border bg-card" }[tone];
  return (
    <div className={`border p-4 ${T}`}>
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 opacity-70" /><span className="mono text-[9.5px] uppercase tracking-widest opacity-80">{label}</span></div>
      <div className="text-3xl font-semibold mt-2 leading-none">{value ?? "—"}</div>
      {sub && <div className="text-[11.5px] text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
};

const Metric = ({ label, value, sub }) => (
  <div className="border border-border p-3">
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-xl font-semibold mt-1">{value ?? "—"}</div>
    {sub && <div className="text-[10.5px] text-muted-foreground">{sub}</div>}
  </div>
);
